// Shared weight/volume <-> pieces conversion helpers (frontend mirror of
// backend/utils/weightPieces.js). Single source of truth on the client for
// converting between amounts in g/kg/ml/L and whole pieces of a pack whose
// size is given by a variant's weight_value + weight_unit.
//
// Model invariant: stock_quantity, cart quantities and order quantities are
// ALWAYS in pieces of packs. A pack's size is weight_value + weight_unit.

const COUNT_UNITS = ['pc', 'piece', 'pieces', 'pcs'];

export function normalizeUnit(unit) {
  if (unit == null) return null;
  const u = String(unit).trim().toLowerCase();
  if (u === 'l') return 'l';
  if (COUNT_UNITS.includes(u)) return 'pcs';
  return u || null;
}

export function unitDimension(unit) {
  const u = normalizeUnit(unit);
  if (u === 'pcs') return 'count';
  if (u === 'g' || u === 'kg') return 'mass';
  if (u === 'ml' || u === 'l') return 'volume';
  return null;
}

export function isCountUnit(unit) {
  return unitDimension(unit) === 'count';
}

export function isWeightUnit(unit) {
  const dimension = unitDimension(unit);
  return dimension === 'mass' || dimension === 'volume';
}

export function toBaseAmount(amount, unit) {
  const u = normalizeUnit(unit);
  const dimension = unitDimension(u);
  if (!dimension) return null;
  let baseAmount = Number(amount);
  if (u === 'kg' || u === 'l') baseAmount *= 1000;
  return { dimension, baseAmount };
}

function roundPieces(value, roundMode, epsilon = 1e-9) {
  if (!Number.isFinite(value)) return null;
  const nearest = Math.round(value);
  if (Math.abs(value - nearest) <= epsilon) return nearest;
  if (roundMode === 'round') return Math.round(value);
  if (roundMode === 'none') return value;
  return Math.ceil(value);
}

export function weightToPieces(amount, unit, weightValue, weightUnit, { roundMode = 'up' } = {}) {
  const produced = toBaseAmount(amount, unit);
  if (!produced) return null;
  if (produced.dimension === 'count') return Number(amount);

  const pack = toBaseAmount(weightValue, weightUnit);
  if (!pack || pack.dimension === 'count' || pack.dimension !== produced.dimension || pack.baseAmount <= 0) {
    return null;
  }
  return roundPieces(produced.baseAmount / pack.baseAmount, roundMode);
}

export function piecesToWeight(pieces, weightValue, weightUnit) {
  const dimension = unitDimension(weightUnit);
  if (!dimension || dimension === 'count') return null;
  const count = Number(pieces);
  const weight = Number(weightValue);
  if (!Number.isFinite(count) || !Number.isFinite(weight)) return null;
  return count * weight;
}

// Reverse conversion: express a piece count as an amount in an output unit.
// e.g. 1600 pieces of a 25 g pack = 40 kg (output 'kg') or 40000 (output 'g').
// Returns null when not convertible (count units, mismatched dimensions, etc.).
// Used by the weight/pcs inputs in quick order, inventory and fulfillment to
// back-fill the weight/qty field from a pcs-only entry.
export function weightFromPieces(pieces, weightValue, weightUnit, outputUnit) {
  const pack = toBaseAmount(weightValue, weightUnit);
  const out = normalizeUnit(outputUnit);
  const dimension = unitDimension(out);
  if (!pack || !dimension || dimension === 'count' || pack.dimension !== dimension || pack.baseAmount <= 0) {
    return null;
  }
  const count = Number(pieces);
  if (!Number.isFinite(count)) return null;
  const totalBase = count * pack.baseAmount;
  return (out === 'kg' || out === 'l') ? totalBase / 1000 : totalBase;
}

export function toKilogramEquivalent(weightValue, weightUnit) {
  const u = normalizeUnit(weightUnit);
  const w = Number(weightValue);
  if (u === 'g') return w / 1000;
  if (u === 'ml') return (w * 0.9) / 1000;
  if (u === 'l') return w * 0.9;
  return w;
}
